import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Owner } from '../../../../../@types/Owner';
import { PROPERTY_TYPE, PROPERTY_TYPE_LABELS } from '../../../../../@types/Owner';
import {
  updateOwnerOfDpe as updateOwnerOfDpeOwnerPage,
} from '../../../../../store/DpeOwnerReducer';
import {
  addOwnerToDpe,
  removeOwner, setNbOwner,
  setOwner,
  updateOwnerOfDpe,
} from '../../../../../store/DpeReducer';
import { useAppDispatch, useAppSelector } from '../../../../../store/hook';
import type { RootState } from '../../../../../store/store';
import classNames from '../../../../../utils/classNames';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import { isValidEmail, isValidFrenchPhone } from '../../../../../utils/validation';
import { showToast } from '../../../../common/Notification/';
import Popup from '../../../../common/Popup';
import Spinner from '../../../../common/Spinner';
import { DPE_QUALIF_STEPS } from '../dpeQualifStep';

interface Props {
  ownerIndex: number,
  owner: Owner
  formErrors?: { [key: string]: string } | undefined;
  updateOwnerCallback?: (updatedOwner: Owner) => void;
}

const DpeOwnerForm = ({ ownerIndex, owner, formErrors, updateOwnerCallback }: Props) => {
  const dispatch = useAppDispatch();
  const selectedDpe = useAppSelector(state => state.dpe.selectedDpe);
  const currentStep = useAppSelector(state => state.dpe.currentStep);
  const nbOwner = useAppSelector(state => state.dpe.nbOwner);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [isEditDisabled, setIsEditDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [localOwner, setLocalOwner] = useState<Owner>(owner);

  useEffect(() => {
    setLocalOwner(owner);
  }, [owner]);

  useEffect(() => {
    if (!owner.id) {
      const updatedOwner = { ...localOwner, livingIn: localOwner.livingIn };
      setLocalOwner(updatedOwner);
      dispatch(setOwner([updatedOwner, ownerIndex]));
      setIsEditDisabled(false);
    } else if (currentStep === DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS) {
      setIsEditDisabled(false);
    } else if (updateOwnerCallback) {
      setIsEditDisabled(false);
    } else {
      setIsEditDisabled(true);
    }
  }, []);

  useEffect(() => {
    if (currentStep === DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS) {
      setIsEditDisabled(false);
    }
  }, [currentStep]);

  useEffect(() => {
    setErrors(formErrors || {});
  }, [formErrors]);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    const updatedOwner = { ...localOwner, [field]: value };
    setLocalOwner(updatedOwner);
    dispatch(setOwner([updatedOwner, ownerIndex]));
  };

  const handleCancelOwnerBtnClick = () => {
    if (localOwner.id) {
      setIsEditDisabled(true);
    } else {
      dispatch(removeOwner(ownerIndex));
    }
  };

  const deleteOwner = async() => {
    setLoading(true);
    try {
      const response = await fetch(`/api/owners/${localOwner.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        dispatch(removeOwner(ownerIndex));
        dispatch(setNbOwner(nbOwner - 1));
        showToast('success', 'Le propriétaire a bien été supprimé.');
      } else {
        console.error(`Failed to delete owner ${localOwner.id}`);
        showToast('error', 'Une erreur est survenue lors de la suppression du propriétaire.');
      }
    } catch (error) {
      console.error(`Error calling API to delete owner ${localOwner.id}:`, error);
      showToast('error', 'Une erreur est survenue lors de la suppression du propriétaire.');
    }
    setLoading(false);
    setIsConfirmPopupOpen(false);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    // Only validate lastname as required
    if (!localOwner.lastname) {
      newErrors.lastname = 'Le nom est obligatoire';
    }
    
    if (localOwner.email && !isValidEmail(localOwner.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (localOwner.phone && !isValidFrenchPhone(localOwner.phone)) {
      newErrors.phone = 'Le format du téléphone n\'est pas valide';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveOwnerBtnClick = () => {
    if (isEditDisabled) {
      setIsEditDisabled(false);
    } else {
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      let uri = '';
      let method = '';
      if (localOwner.id) {
        uri = `/api/owners/${localOwner.id}`;
        method = 'PUT';
      } else if (selectedDpe?.id) {
        uri = `/api/dpes/${selectedDpe.id}/owners`;
        method = 'POST';
      } else {
        console.error('Cannot save owner: No owner ID or DPE ID available');
        showToast('error', 'Une erreur est survenue: impossible de déterminer l\'ID du DPE');
        setLoading(false);
        return;
      }
      
      const ownerData = {
        firstname: localOwner.firstname,
        lastname: localOwner.lastname,
        postalCode: localOwner.postalCode,
        city: localOwner.city,
        address: localOwner.address,
        livingIn: localOwner.livingIn,
        email: localOwner.email,
        phone: localOwner.phone,
        civility: localOwner.civility,
        floorNumber: localOwner.floorNumber,
        doorNumber: localOwner.doorNumber,
        lotNumber: localOwner.lotNumber,
        surface: localOwner.surface,
        propertyType: localOwner.propertyType ?? null,
      };
      
      fetch(uri, {
        method,
        headers: {
          'Authorization': JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ownerData),
      }).then((_response) => {
        if (!_response.ok) {
          throw new Error(`HTTP error! Status: ${_response.status}`);
        }
        return _response.json();
      })
        .then((_owner) => {
          if (currentStep !== DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS) {
            setIsEditDisabled(true);
          } else {
            setIsEditDisabled(false);
          }
          
          if (localOwner.id) {
            if (currentStep === DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS) {
              // Only dispatch Redux actions if we have a selectedDpe
              if (selectedDpe?.id) {
                dispatch(updateOwnerOfDpeOwnerPage([selectedDpe.id, _owner]));
              }
            } else if (selectedDpe?.id) {
              dispatch(updateOwnerOfDpe([selectedDpe.id, _owner]));
            }
            showToast('success', 'Les informations du propriétaire ont été mises à jour.');
          } else if (selectedDpe?.id) {
            dispatch(addOwnerToDpe([selectedDpe.id, _owner]));
            showToast('success', 'Le propriétaire a été enregistré.');
          }
          
          // Always call the updateOwnerCallback if provided, regardless of the current step
          if (updateOwnerCallback) {
            updateOwnerCallback(_owner);
          }
        }).catch((error) => {
          console.error('Error adding/updating owner:', error);
          showToast('error', 'Une erreur est survenue lors de la mise à jour du propriétaire.');
        }).finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className={'flex flex-col'}>
      <table className={'w-full flex-1 border-2 border-white'}>
        <colgroup>
          <col span={1} style={{ width: '50%' }}/>
          <col span={1} style={{ width: '50%' }}/>
        </colgroup>
        <thead className="bg-[#4472C4] text-white">
          <tr>
            <th colSpan={2} className={'border-2 border-white pl-2 text-left text-2xl leading-[3rem] font-bold'} key={'identifier'}>PROPRIÉTAIRE {currentStep !== DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS && `N°${  ownerIndex + 1}`}</th>
          </tr>
        </thead>
        <tbody>
          <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Civilité
            </td>
            <td className={'border-2 border-white p-2'}>
              <select id={`civilite-${  ownerIndex}`} name={`civilite-${  ownerIndex}`}
                className={`w-full rounded-xl border-0 py-1.5 pr-10 pl-3 text-gray-900 ring-1 ring-gray-300 ring-inset focus:ring-2 sm:text-sm sm:leading-6 ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('civility', parseInt(e.target.value, 10))}
                disabled={isEditDisabled}
                value={localOwner.civility}
              >
                <option value={1}>M.</option>
                <option value={2}>Mme</option>
              </select>
            </td>
          </tr>
          <tr className={'bg-[#E9EBF5]'}>
            <td className={'border-2 border-white p-2'}>
              Le bien est *
            </td>
            <td className={'border-2 border-white p-2'}>
              <div className={'flex flex-col'}>
                <select id={`propertyType-${ownerIndex}`} name={`propertyType-${ownerIndex}`}
                  className={`w-full rounded-xl border-0 py-1.5 pr-10 pl-3 text-gray-900 ring-1 ring-gray-300 ring-inset focus:ring-2 sm:text-sm sm:leading-6 ${
                    isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                  onChange={(e) => handleInputChange('propertyType', parseInt(e.target.value, 10))}
                  disabled={isEditDisabled}
                  value={localOwner.propertyType ?? PROPERTY_TYPE.UNKNOWN}
                >
                  <option value={PROPERTY_TYPE.UNKNOWN}>{PROPERTY_TYPE_LABELS[PROPERTY_TYPE.UNKNOWN]}</option>
                  <option value={PROPERTY_TYPE.HOUSE}>{PROPERTY_TYPE_LABELS[PROPERTY_TYPE.HOUSE]}</option>
                  <option value={PROPERTY_TYPE.APARTMENT}>{PROPERTY_TYPE_LABELS[PROPERTY_TYPE.APARTMENT]}</option>
                </select>
                {errors.propertyType && <span className="text-red-500">{errors.propertyType}</span>}
              </div>
            </td>
          </tr>
          <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Prénom
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="firstname" id={`firstname${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('firstname', e.target.value)}
                value={localOwner.firstname ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
              />
            </td>
          </tr>
          <tr className={'bg-[#E9EBF5]'}>
            <td className={'border-2 border-white p-2'}>
              Nom *
            </td>
            <td className={'border-2 border-white p-2'}>
              <div className={'flex flex-col'}>
                <input type="text" name="lastname" id={`lastname${ownerIndex}`}
                  className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                    isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                  onChange={(e) => handleInputChange('lastname', e.target.value)}
                  value={localOwner.lastname ?? ''}
                  disabled={isEditDisabled}
                  autoComplete="nope"
                />
                {errors.lastname && <span className="text-red-500">{errors.lastname}</span>}
              </div>
            </td>
          </tr>
          <tr className={'bg-[#DEEBF7]'}>
            <td className={'border-2 border-white p-2'}>
              L'adresse postale de résidence du propriétaire est-elle la même que l'adresse de l'immeuble ?
            </td>
            <td className={'border-2 border-white p-2'}>
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="ownerSameAddress"
                  name={`ownerSameAddress${  ownerIndex}`}
                  value={localOwner.livingIn}
                  onChange={(event) => handleInputChange('livingIn', event.target.value === 'true')}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Oui"
                    disabled={isEditDisabled}
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="Non"
                    disabled={isEditDisabled}
                  />
                </RadioGroup>
              </FormControl>
            </td>
          </tr>
          { !localOwner.livingIn && <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Adresse postale de vie du propriétaire
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="address" id={`address${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('address', e.target.value)}
                value={localOwner.address ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
              />
            </td>
          </tr>}
          { !localOwner.livingIn && <tr className={'bg-[#E9EBF5]'}>
            <td className={'border-2 border-white p-2'}>
              Code postal
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="number" name="postalcode" id={`postalcode${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                value={localOwner.postalCode ?? ''}
                disabled={isEditDisabled}
              />
            </td>
          </tr>}
          { !localOwner.livingIn && <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Ville
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="city" id={`city${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('city', e.target.value)}
                value={localOwner.city ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
              />
            </td>
          </tr>}
          <tr className={'bg-[#E9EBF5]'}>
            <td className={'border-2 border-white p-2'}>
              Téléphone
            </td>
            <td className={'border-2 border-white p-2'}>
              <div className={'flex flex-col'}>
                <input type="text" name="phone" id={`phone${  ownerIndex}`}
                  className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                    isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  value={localOwner.phone ?? ''}
                  disabled={isEditDisabled}
                  autoComplete="nope"
                />
                {errors?.phone && <span className="text-red-500">{errors.phone}</span>}
              </div>
            </td>
          </tr>
          <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Email *
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="mail" id={`mail${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('email', e.target.value)}
                value={localOwner.email ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
              />
              {errors?.email && <span className="text-red-500">{errors.email}</span>}
            </td>
          </tr>
          <tr className={'bg-[#E9EBF5]'}>
            <td className={'border-2 border-white p-2'}>
              Etage
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="floorNumber" id={`floorNumber${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('floorNumber', e.target.value)}
                value={localOwner.floorNumber ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
              />
            </td>
          </tr>
          <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Numéro de porte
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="doorNumber" id={`doorNumber${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('doorNumber', e.target.value)}
                value={localOwner.doorNumber ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
              />
            </td>
          </tr>
          <tr className={'bg-[#CFD5EA]'}>
            <td className={'border-2 border-white p-2'}>
              Numéro de lot
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="text" name="lotNumber" id={`lotNumber${ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('lotNumber', e.target.value)}
                value={localOwner.lotNumber ?? ''}
                disabled={isEditDisabled}
                autoComplete="nope"
                maxLength={50}
              />
            </td>
          </tr>
          <tr className={'bg-[#E9EBF5]'}>
            <td className={'border-2 border-white p-2'}>
              Surface (m²)
            </td>
            <td className={'border-2 border-white p-2'}>
              <input type="number" name="surface" id={`surface${  ownerIndex}`}
                className={`w-full rounded-xl border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset focus:ring-inset ${
                  isEditDisabled ? 'border-slate-200 bg-slate-50 text-slate-400 shadow-none' : ''}`}
                onChange={(e) => handleInputChange('surface', parseInt(e.target.value, 10))}
                value={localOwner.surface?.toString() ?? ''}
                disabled={isEditDisabled}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className='mt-2'>* Champ obligatoire</div>
      <div className={`flex justify-center${  ((currentStep === DPE_QUALIF_STEPS.DISPLAY_OWNERS_DETAILS) && !isEditDisabled) ? ' grid grid-cols-2 gap-2' : ''}`}>
        {!isEditDisabled && (nbOwner > 1 || localOwner.id) && currentStep !== DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS &&
          <button type='button'
            className={'m-auto mt-2 w-full rounded-xl bg-red-600 px-5 py-3 text-center text-lg font-semibold text-white shadow-sm'}
            onClick={handleCancelOwnerBtnClick}
          >{currentStep === DPE_QUALIF_STEPS.DISPLAY_OWNERS_DETAILS ? 'Annuler' : 'Supprimer'}</button>
        }
        {(currentStep === DPE_QUALIF_STEPS.DISPLAY_OWNERS_DETAILS || currentStep === DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS) &&
          <div 
            className={
              classNames(
                'flex justify-center w-full',
                currentStep === DPE_QUALIF_STEPS.DISPLAY_OWNERS_DETAILS && isEditDisabled && nbOwner > 1 && ' grid grid-cols-2 gap-2',
              )}>
            {currentStep === DPE_QUALIF_STEPS.DISPLAY_OWNERS_DETAILS && nbOwner > 1 && isEditDisabled &&
              <button type='button'
                className={'m-auto mt-2 w-full rounded-xl bg-red-600 px-5 py-3 text-center text-lg font-semibold text-white shadow-sm'}
                onClick={() => setIsConfirmPopupOpen(true)}
              >Supprimer</button>
            }
            <button type='button'
              className={classNames('rounded-xl py-3 px-8 text-lg font-semibold text-white text-center shadow-sm mt-2 m-auto',
                isEditDisabled ? ' bg-rgpd-blue-dark' : ' bg-rgpd-green', currentStep === DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS ? 'w-1/2' : 'w-full' )}
              onClick={handleSaveOwnerBtnClick}
            >
              {loading ? (
                <>
                  <Spinner aria-label="Center-aligned spinner" size="lg" className='mr-1'/>Chargement...
                </>
              ) : isEditDisabled && currentStep !== DPE_QUALIF_STEPS.MODIFICATION_SINGLE_OWNER_DETAILS ? 'Modifier' : 'Enregistrer' }
            </button>
          </div>
        }
      </div>
      <Popup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
        title="Confirmation de suppression"
        additionalClass="w-1/2"
        children={
          <>
            <p className="mb-4 text-gray-600">Êtes-vous sûr de vouloir supprimer ce propriétaire ?</p>
            <div className="flex justify-between">
              <div className="flex grow justify-start">
                <button
                  className="mt-4 mr-1 w-full rounded bg-gray-300 px-4 py-2 font-bold text-gray-700 md:w-auto"
                  onClick={() => setIsConfirmPopupOpen(false)}
                >
                  Annuler
                </button>
              </div>
              <div className="flex grow justify-end">
                <button
                  className="focus:shadow-outline mt-4 ml-1 w-full rounded bg-rgpd-green px-4 py-2 font-bold text-white opacity-90 focus:outline-none md:w-auto"
                  onClick={() => deleteOwner()}
                >
                  {loading && <Spinner aria-label="Center-aligned spinner" size="sm" className={'mr-2'}/>}
                  <span>Confirmer</span>
                </button>
              </div>
            </div>
          </>
        }
      />
    </div>
  );
};

export default DpeOwnerForm;